import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs/';
import {
  AddFamilyMember$Request,
  AddFamilyMember$Response,
  Country,
  GetFamilyMemberList$Response,
  GetProfile$Response,
  GetUserLocalDate$Response,
  Lang,
  LoginMethod,
  MeasurementSystem,
  ModifyFamilyMember$Response,
  PasswordAuth$Response,
  Profile,
  ProfileId,
  RecalculateRecipeAmounts$Response,
  RemoveFamilyMember$Response,
  Role,
  ToggleShowRecipeCarbs$Response,
  UpdateMeasurementSystem$Response,
  UpdatePassword$Response,
  UpdateProfile$Response,
  UpdateProfileExerciseLevel$Request,
  UpdateProfileExerciseLevel$Response,
  UpdateProfileWeight$Request,
  UpdateProfileWeight$Response,
  UserId,
  WhoAmI$Response,
  WhoAmI$AppConfig,
  Feature,
} from '@api/types';
import { ErrorService } from '../../api/error.service';
import { ApiService } from '../../api/api.service';
import { NotificationService } from '../notification/notification.service';

@Injectable()
export class ProfileService {
  private userProfile: Profile;
  private country: Country;
  private lang: Lang;
  private userRole: Role;
  private loginMethod: LoginMethod;
  private userId: UserId;
  private mainProfileId: ProfileId;
  private active: boolean;
  private config: WhoAmI$AppConfig;
  public features: Feature[];
  private userLocalDate: string;
  private measurementSystem: MeasurementSystem;
  private showRecipeCarbs: boolean;
  private isPasswordSet: boolean;
  private familyMembers: Profile[] = [];
  private localDateRequestInProgress: boolean;
  // replaySubjects for streams
  private userProfileSubject: Subject<GetProfile$Response> = new ReplaySubject<GetProfile$Response>(null);
  private userRoleSubject: Subject<WhoAmI$Response> = new ReplaySubject<WhoAmI$Response>(null);
  private familyMembersSubject: Subject<GetFamilyMemberList$Response> = new ReplaySubject<GetFamilyMemberList$Response>(null);
  private userLocalDateSubject: Subject<GetUserLocalDate$Response> = new ReplaySubject<GetUserLocalDate$Response>(null);

  constructor(
    private router: Router,
    private api: ApiService,
    private notificationService: NotificationService,
    private error: ErrorService
  ) {}

  whoAmI(forceFetch?: boolean): Observable<WhoAmI$Response> {
    if (!this.userRole || forceFetch) {
      let obs = this.api.user.whoAmI();
      // we don't have userRole yet. Ask it from API
      obs.subscribe(
        (response) => {
          // update success, replace service data with new value and send to stream
          if (response && response.role) {
            // save data to service first for cached response next time
            this.userRole = response.role;
            this.lang = response.lang;
            this.userId = response.userId;
            this.mainProfileId = response.mainProfileId;
            this.country = response.country;
            this.loginMethod = response.loginMethod;
            this.active = response.active;
            this.config = response.config;

            // send data to stream
            this.userRoleSubject.next({
              country: this.country,
              lang: this.lang,
              loginMethod: this.loginMethod,
              role: this.userRole,
              userId: this.userId,
              mainProfileId: this.mainProfileId,
              active: this.active,
              config: this.config,
              features: this.features,
            });
          } else {
            // send data to stream
            this.userRoleSubject.next(null);
          }
        },
        (err) => {
          this.userRoleSubject.error(err);
        }
      );
    } else {
      // we already have userProfile so send that to stream
      this.userRoleSubject.next({
        country: this.country,
        lang: this.lang,
        loginMethod: this.loginMethod,
        role: this.userRole,
        userId: this.userId,
        mainProfileId: this.mainProfileId,
        active: this.active,
        config: this.config,
        features: this.features,
      });
    }

    return this.userRoleSubject;
  }

  /*
        Get user profile
        Unless we already have users profile/measurementSystem, then fetch it from API
     */
  getProfile(forceFetch?: boolean): Observable<GetProfile$Response> {
    if (!this.userProfile || forceFetch) {
      // we don't have userProfile yet. Ask it from API
      this.api.user.getProfile().subscribe((response) => {
        // update success, replace service data with new value and send to stream
        if (response && response.profile) {
          // save data to service first for cached response next time
          this.userProfile = response.profile;
          this.measurementSystem = response.measurementSystem;
          this.showRecipeCarbs = response.showRecipeCarbs;
          this.isPasswordSet = response.isPasswordSet;

          // send data to stream
          this.userProfileSubject.next({
            profile: this.userProfile,
            measurementSystem: this.measurementSystem,
            showRecipeCarbs: this.showRecipeCarbs,
            isPasswordSet: this.isPasswordSet,
          });
        }
      });
    } else {
      // we already have userProfile so send that to stream
      this.userProfileSubject.next({
        profile: this.userProfile,
        measurementSystem: this.measurementSystem,
        showRecipeCarbs: this.showRecipeCarbs,
        isPasswordSet: this.isPasswordSet,
      });
    }

    return this.userProfileSubject;
  }

  /*
     Fetch list of family members from API
     */
  getFamilyMembers(forceFetch?: boolean): Observable<GetFamilyMemberList$Response> {
    if (!this.familyMembers || forceFetch) {
      this.api.family.getFamilyMemberList().subscribe((response) => {
        // update success, replace service data with new value
        if (response) {
          // save data to service first for cached response next time
          this.familyMembers = response.familyRows;

          // send data to stream
          this.familyMembersSubject.next({
            familyRows: this.familyMembers,
          });
        }
      });
    } else {
      // send data to stream
      this.familyMembersSubject.next({
        familyRows: this.familyMembers,
      });
    }

    return this.familyMembersSubject;
  }

  getLocalDate(forceFetch: boolean = true): Observable<GetUserLocalDate$Response> {
    if ((!this.userLocalDate || forceFetch) && !this.localDateRequestInProgress) {
      this.localDateRequestInProgress = true;
      this.api.user.getUserLocalDate().subscribe((response) => {
        this.localDateRequestInProgress = false;
        if (response) {
          this.userLocalDate = response.localDate;

          this.userLocalDateSubject.next({
            localDate: this.userLocalDate,
          });
        }
      });
    } else {
      this.userLocalDateSubject.next({
        localDate: this.userLocalDate,
      });
    }

    return this.userLocalDateSubject;
  }

  /*
        Update user profile
     */
  updateProfile(profile: Profile): Observable<UpdateProfile$Response> {
    let request = {
      profile: ProfileService.parseProfileObject(profile),
    };
    let obs = this.api.user.updateProfile(request);

    obs.subscribe(
      (response) => {
        // update success, replace service data with new value and send to stream
        if (response && response.profile) {
          this.userProfile = response.profile;

          // send data to stream
          this.userProfileSubject.next({
            profile: this.userProfile,
            measurementSystem: this.measurementSystem,
            showRecipeCarbs: this.showRecipeCarbs,
            isPasswordSet: this.isPasswordSet,
          });
        }
      },
      (error) => {}
    );

    return obs;
  }

  /*
        Update only a small portion of profile info(only for profile overview)
     */
  updateProfileWeight(value): Observable<UpdateProfileWeight$Response> {
    let request: UpdateProfileWeight$Request = {
      profileId: value.id,
      weight: value.weight,
      recalculateCalories: value.updateCalories,
    };

    let obs = this.api.user.updateProfileWeight(request);

    obs.subscribe(
      (response) => {
        // update success, replace service data with new value
        if (response && response.profile) {
          if (value.id == this.userProfile.id) {
            // we updated current user
            this.userProfile = response.profile;

            // send to stream
            this.userProfileSubject.next({
              profile: this.userProfile,
              measurementSystem: this.measurementSystem,
              showRecipeCarbs: this.showRecipeCarbs,
              isPasswordSet: this.isPasswordSet,
            });
          } else {
            // we updated a family member
            let member = this.familyMembers.find((member) => {
              return member.id == value.id;
            });
            if (member) {
              member = response.profile;
            }

            this.familyMembersSubject.next({
              familyRows: this.familyMembers,
            });
          }

          this.notificationService.weightUpdated();
        }
      },
      (err) => {}
    );

    return obs;
  }

  /*
     Update only a small portion of profile info(only for profile overview)
     */
  updateProfileExerciseLevel(value): Observable<UpdateProfileExerciseLevel$Response> {
    let request: UpdateProfileExerciseLevel$Request = {
      profileId: value.id,
      exerciseLevel: value.exerciseLevel.toString(),
    };

    let obs = this.api.user.updateProfileExerciseLevel(request);

    obs.subscribe(
      (response) => {
        // update success, replace service data with new value
        if (response && response.profile) {
          if (value.id == this.userProfile.id) {
            // we updated current user
            this.userProfile = response.profile;

            // send to stream
            this.userProfileSubject.next({
              profile: this.userProfile,
              measurementSystem: this.measurementSystem,
              showRecipeCarbs: this.showRecipeCarbs,
              isPasswordSet: this.isPasswordSet,
            });
          } else {
            // we updated a family member
            let member = this.familyMembers.find((member) => {
              return member.id == value.id;
            });
            if (member) {
              member = response.profile;
            }

            this.familyMembersSubject.next({
              familyRows: this.familyMembers,
            });
          }

          this.notificationService.weightUpdated();
        }
      },
      (err) => {}
    );

    return obs;
  }

  /*
        Update measurementSystem for user and his/her family
     */
  updateMeasurementSystem(measurementSystem: MeasurementSystem): Observable<UpdateMeasurementSystem$Response> {
    let request = {
      measurementSystem: measurementSystem.toString(),
    };
    let obs = this.api.user.updateMeasurementSystem(request as any); //TODO remove "as any" when we have aliases set for request types

    obs.subscribe((response) => {
      // update success, replace service data with new value
      if (response) {
        this.measurementSystem = measurementSystem;
      }
    });

    return obs;
  }

  /*
     Update measurementSystem for user and his/her family
     */
  updateShowRecipeCarbs(showRecipeCarbs: boolean): Observable<ToggleShowRecipeCarbs$Response> {
    let request = {
      showRecipeCarbs: showRecipeCarbs,
    };
    let obs = this.api.user.toggleShowRecipeCarbs(request);

    obs.subscribe((response) => {
      // update success, replace service data with new value
      if (response && response.isUpdated) {
        this.showRecipeCarbs = showRecipeCarbs;
        // send to stream
        this.userProfileSubject.next({
          profile: this.userProfile,
          measurementSystem: this.measurementSystem,
          showRecipeCarbs: this.showRecipeCarbs,
          isPasswordSet: this.isPasswordSet,
        });
      }
    });

    return obs;
  }

  updatePassword(password: string): Observable<UpdatePassword$Response> {
    let request = {
      password: password,
    };

    let obs = this.api.user.updatePassword(request);

    obs.subscribe((res) => {
      if (res) {
        this.isPasswordSet = true;
      }
    });

    return obs;
  }

  /*
        Build request object and push to API to add new family member
     */
  addFamilyMember(member: Profile): Observable<AddFamilyMember$Response> {
    let request: AddFamilyMember$Request = {
      profile: ProfileService.parseProfileObject(member),
    };

    let obs = this.api.family.addFamilyMember(request);

    obs.subscribe(
      (res) => {
        if (res) {
          this.familyMembers.push(res.profile);
          // send new familyMembers array to stream
          this.familyMembersSubject.next({
            familyRows: this.familyMembers,
          });
        }
      },
      (error) => {}
    );

    return obs;
  }

  /*
        Remove familyMember by provided ID
     */
  removeFamilyMember(id: ProfileId): Observable<RemoveFamilyMember$Response> {
    let request = {
      profileId: id,
    };
    let obs = this.api.family.removeFamilyMember(request);

    obs.subscribe((response) => {
      // update success, replace service data with new value
      if (response) {
        for (let index in this.familyMembers) {
          if (this.familyMembers[index].id == id) {
            this.familyMembers.splice(parseInt(index), 1);
            break;
          }
        }
        // send new familyMembers array to stream
        this.familyMembersSubject.next({
          familyRows: this.familyMembers,
        });
      }
    });

    return obs;
  }

  /*
        Update family member data
     */
  updateFamilyMember(member: Profile): Observable<ModifyFamilyMember$Response> {
    let request: AddFamilyMember$Request = {
      profile: ProfileService.parseProfileObject(member),
    };
    let obs = this.api.family.modifyFamilyMember(request);

    obs.subscribe(
      (response) => {
        // update success, replace service data with new value
        if (response) {
          for (let index in this.familyMembers) {
            if (this.familyMembers[index].id == member.id) {
              this.familyMembers[index] = response.profile;
              break;
            }
          }
          this.familyMembersSubject.next({
            familyRows: this.familyMembers,
          });
        }
      },
      (error) => {}
    );

    return obs;
  }

  /*
        Recalculate recipe calories
     */
  recalculateRecipeAmounts(): Observable<RecalculateRecipeAmounts$Response> {
    let obs = this.api.user.recalculateRecipeAmounts({
      recalculateCalories: true,
    });

    obs.subscribe(
      (response) => {},
      (error) => {}
    );

    return obs;
  }

  /*
        Log user in using email & password
     */
  logIn(email: string, password: string): Observable<PasswordAuth$Response> {
    let request = {
      email: email,
      password: password,
    };
    let obs = this.api.oauth.passwordAuth(request);

    obs.subscribe((res) => {
      if (res && res.role) {
        this.userRole = res.role;
        this.lang = res.lang;
        this.userId = res.userId;
      }
    });

    return obs;
  }

  /*
        Log user out
     */
  logOut() {
    this.api.user.logout().subscribe((res) => {
      this.router.navigate(['welcome']);
    });
  }

  getUserRoleSubject(): Subject<WhoAmI$Response> {
    return this.userRoleSubject;
  }

  /*
        Parse profile object before sending it to the server. Might be here temporarily
        Converts enums to string to feed to HTTP request. workaround for enum type mismatch
     */
  static parseProfileObject(obj: Profile): Profile {
    obj.id = obj.id >= 0 ? obj.id : null;
    obj.lastWeight.weightDate = null;
    obj.lastName = obj.lastName ? obj.lastName : '';
    obj.exerciseLevel = obj.exerciseLevel ? (obj.exerciseLevel.toString() as any) : null;
    obj.sex = obj.sex ? (obj.sex.toString() as any) : null;
    obj.allergens = obj.allergens ? obj.allergens : [];

    return obj;
  }
}
